import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Cpu, Send, Mic, Square, Plus, LogOut, Trash2, ListChecks,
  Volume2, VolumeX, MessageSquare, Loader2, Check, User, ArrowLeft,
} from "lucide-react";
import Orb from "@/components/Orb";
import { useVoice } from "@/hooks/useVoice";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";

function uid() { return Math.random().toString(36).slice(2) + Date.now().toString(36); }

export default function Channel({ mode }) {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const isPersonal = mode === "personal";

  const [sessionId, setSessionId] = useState(uid());
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [orbState, setOrbState] = useState("idle");
  const [voiceOut, setVoiceOut] = useState(true);
  const scrollRef = useRef(null);

  const { recording, transcribing, startRecording, stopRecording, speak, stopSpeaking } = useVoice();

  // redirect if personal but not authed
  useEffect(() => {
    if (isPersonal && !loading && !user) navigate("/auth");
  }, [isPersonal, loading, user, navigate]);

  const loadSessions = useCallback(async () => {
    if (!isPersonal || !user) return;
    try {
      const { data } = await api.get("/sessions");
      setSessions(data.sessions);
    } catch { /* ignore */ }
  }, [isPersonal, user]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const openSession = async (sid) => {
    setSessionId(sid);
    const { data } = await api.get(`/sessions/${sid}/messages`);
    setMessages(data.messages);
  };

  const newChat = () => { setSessionId(uid()); setMessages([]); };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    await api.delete(`/sessions/${sid}`);
    if (sid === sessionId) newChat();
    loadSessions();
  };

  const sendMessage = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    setStreaming(true);
    setOrbState("thinking");
    stopSpeaking();

    let acc = "";
    try {
      const token = localStorage.getItem("archon_token");
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ session_id: sessionId, message: content, mode }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            acc += payload.delta;
            setOrbState("speaking");
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: acc };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "⚠️ Connection error. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
      setOrbState("idle");
      if (voiceOut && acc) {
        setOrbState("speaking");
        speak(acc, () => setOrbState("idle"));
      }
      loadSessions();
    }
  };

  const handleMic = async () => {
    if (recording) {
      setOrbState("thinking");
      const text = await stopRecording();
      setOrbState("idle");
      if (text) sendMessage(text);
    } else {
      stopSpeaking();
      setOrbState("listening");
      await startRecording();
    }
  };

  const empty = messages.length === 0;

  return (
    <div className="h-screen w-full flex bg-[#050505] grid-bg overflow-hidden">
      {/* Sidebar (personal only) */}
      {isPersonal && (
        <aside className="hidden md:flex w-64 flex-col border-r border-blue-900/30 bg-[#0A0E17]/80 p-4" data-testid="sidebar">
          <div className="flex items-center gap-2 mb-6 cursor-pointer" onClick={() => navigate("/")}>
            <Cpu className="text-cyan-400" size={20} />
            <span className="font-display font-bold tracking-widest">ARCHON</span>
          </div>
          <button
            data-testid="new-chat-btn" onClick={newChat}
            className="flex items-center gap-2 justify-center bg-blue-600 hover:bg-blue-500 text-white rounded-lg py-2.5 text-sm font-medium transition-all hover:shadow-[0_0_18px_rgba(59,130,246,0.4)]"
          >
            <Plus size={16} /> New Chat
          </button>

          <p className="mono-label mt-6 mb-2">History</p>
          <div className="flex-1 overflow-y-auto space-y-1 -mr-2 pr-2">
            {sessions.map((s) => (
              <div
                key={s.id} onClick={() => openSession(s.id)}
                data-testid={`session-${s.id}`}
                className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm transition-colors ${s.id === sessionId ? "bg-blue-500/15 text-cyan-300" : "text-slate-400 hover:bg-white/5"}`}
              >
                <MessageSquare size={14} className="shrink-0" />
                <span className="truncate flex-1">{s.title}</span>
                <Trash2
                  size={14} onClick={(e) => deleteSession(s.id, e)}
                  className="opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity"
                />
              </div>
            ))}
            {sessions.length === 0 && <p className="text-slate-600 text-xs px-3">No conversations yet.</p>}
          </div>

          <div className="border-t border-blue-900/30 pt-3 mt-3 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-500/20 grid place-items-center text-cyan-300"><User size={16} /></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
            <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="text-slate-500 hover:text-red-400">
              <LogOut size={16} />
            </button>
          </div>
        </aside>
      )}

      {/* Main */}
      <main className="flex-1 flex flex-col relative min-w-0">
        {/* Top bar */}
        <header className="flex items-center justify-between px-5 py-4 border-b border-blue-900/30">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="text-slate-400 hover:text-cyan-300" data-testid="back-btn">
              <ArrowLeft size={18} />
            </button>
            <div>
              <p className="font-display font-semibold leading-tight">{isPersonal ? "Personal ARCHON" : "ARCHON — Free Channel"}</p>
              <p className="mono-label">{streaming ? "PROCESSING…" : "SYS.ONLINE"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="voice-toggle-btn"
              onClick={() => { setVoiceOut(!voiceOut); stopSpeaking(); }}
              className={`p-2 rounded-full border transition-all ${voiceOut ? "border-cyan-400/50 text-cyan-300 bg-cyan-400/10" : "border-blue-900/40 text-slate-500"}`}
              title="Voice replies"
            >
              {voiceOut ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            {!isPersonal && (
              <button
                data-testid="goto-personal-btn"
                onClick={() => navigate(user ? "/system" : "/auth")}
                className="mono-label border border-blue-500/40 px-3 py-1.5 rounded-full hover:bg-blue-500/10 transition-all"
              >
                My System
              </button>
            )}
          </div>
        </header>

        {/* Chat area */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          {empty ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <Orb state={orbState} size={170} />
              <h2 className="font-display text-2xl font-bold mt-10">
                {isPersonal ? `Hello ${user?.name || ""}, how can I help?` : "How can I assist you?"}
              </h2>
              <p className="text-slate-400 mt-2">Type a message or tap the mic to speak.</p>
              <div className="flex flex-wrap gap-2 justify-center mt-6 max-w-lg">
                {["Plan my day", "Explain quantum computing", "Write a short poem", "Give me a productivity tip"].map((s) => (
                  <button
                    key={s} data-testid={`suggestion-${s}`}
                    onClick={() => sendMessage(s)}
                    className="text-sm text-slate-300 border border-blue-900/40 rounded-full px-4 py-2 hover:bg-blue-500/10 hover:border-blue-500/50 transition-all"
                  >{s}</button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6" data-testid="messages-list">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-3 rise ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`message-${m.role}`}>
                  {m.role === "assistant" && (
                    <div className="w-9 h-9 rounded-full shrink-0 grid place-items-center bg-gradient-to-br from-cyan-400/30 to-blue-600/30 border border-cyan-400/40">
                      <Cpu size={16} className="text-cyan-300" />
                    </div>
                  )}
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-blue-600 text-white rounded-br-sm" : "glass text-slate-100 rounded-bl-sm"}`}>
                    {m.content || (streaming && i === messages.length - 1 ? (
                      <span className="inline-flex gap-1 items-center"><span className="dot" /><span className="dot" /><span className="dot" /></span>
                    ) : "")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Input bar */}
        <div className="px-4 sm:px-8 py-5 border-t border-blue-900/30 bg-[#0A0E17]/60 backdrop-blur">
          <div className="max-w-3xl mx-auto flex items-end gap-3">
            <div className="flex-1 flex items-end bg-slate-950/60 border border-blue-900/50 focus-within:border-cyan-400 rounded-2xl px-4 py-2 transition-all">
              <textarea
                data-testid="chat-input"
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder={recording ? "Listening…" : transcribing ? "Transcribing…" : "Message ARCHON…"}
                disabled={recording || transcribing}
                className="flex-1 bg-transparent outline-none resize-none text-slate-50 placeholder-slate-500 py-1.5 max-h-32"
              />
            </div>
            <button
              data-testid="mic-btn"
              onClick={handleMic}
              disabled={transcribing}
              className={`shrink-0 w-12 h-12 rounded-full grid place-items-center transition-all duration-300 ${recording ? "bg-red-500 shadow-[0_0_24px_rgba(239,68,68,0.6)] animate-pulse" : "border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10"}`}
              title="Voice input"
            >
              {transcribing ? <Loader2 size={20} className="animate-spin" /> : recording ? <Square size={18} /> : <Mic size={20} />}
            </button>
            <button
              data-testid="send-btn"
              onClick={() => sendMessage()}
              disabled={streaming || !input.trim()}
              className="shrink-0 w-12 h-12 rounded-full grid place-items-center bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white transition-all hover:shadow-[0_0_22px_rgba(59,130,246,0.5)]"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </main>

      {/* Tasks panel (personal) */}
      {isPersonal && <TasksPanel />}
    </div>
  );
}

function TasksPanel() {
  const [tasks, setTasks] = useState([]);
  const [title, setTitle] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/tasks"); setTasks(data.tasks); } catch { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    const { data } = await api.post("/tasks", { title: title.trim() });
    setTasks((t) => [data.task, ...t]);
    setTitle("");
  };
  const toggle = async (task) => {
    const { data } = await api.patch(`/tasks/${task.id}`, { done: !task.done });
    setTasks((t) => t.map((x) => (x.id === task.id ? data.task : x)));
  };
  const remove = async (id) => {
    await api.delete(`/tasks/${id}`);
    setTasks((t) => t.filter((x) => x.id !== id));
  };

  return (
    <aside className="hidden lg:flex w-72 flex-col border-l border-blue-900/30 bg-[#0A0E17]/50 p-4" data-testid="tasks-panel">
      <div className="flex items-center gap-2 mb-1">
        <ListChecks className="text-cyan-400" size={18} />
        <h3 className="font-display font-semibold">Tasks</h3>
      </div>
      <p className="mono-label mb-4">MISSION QUEUE</p>

      <form onSubmit={add} className="flex gap-2 mb-4">
        <input
          data-testid="task-input"
          value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Add a task…"
          className="flex-1 bg-slate-950/60 border border-blue-900/50 focus:border-cyan-400 outline-none rounded-lg px-3 py-2 text-sm text-slate-50"
        />
        <button data-testid="add-task-btn" className="w-10 rounded-lg bg-blue-600 hover:bg-blue-500 grid place-items-center transition-colors">
          <Plus size={16} />
        </button>
      </form>

      <div className="flex-1 overflow-y-auto space-y-2 -mr-2 pr-2">
        <AnimatePresence>
          {tasks.map((t) => (
            <motion.div
              key={t.id} layout initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}
              data-testid={`task-${t.id}`}
              className="group flex items-center gap-3 border border-blue-900/30 rounded-lg px-3 py-2.5"
            >
              <button
                data-testid={`toggle-task-${t.id}`}
                onClick={() => toggle(t)}
                className={`w-5 h-5 rounded border grid place-items-center shrink-0 transition-colors ${t.done ? "bg-cyan-400 border-cyan-400 text-black" : "border-blue-700"}`}
              >
                {t.done && <Check size={13} />}
              </button>
              <span className={`flex-1 text-sm ${t.done ? "line-through text-slate-600" : "text-slate-200"}`}>{t.title}</span>
              <Trash2 size={14} onClick={() => remove(t.id)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 cursor-pointer transition-opacity" />
            </motion.div>
          ))}
        </AnimatePresence>
        {tasks.length === 0 && <p className="text-slate-600 text-xs">No tasks yet. Add one above.</p>}
      </div>
    </aside>
  );
}
